import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  FormControl,
  Select,
  FormHelperText,
  Box,
  Typography,
  Divider,
  Checkbox,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { loadCanvasSizes } from "../Helpers/loadCanvasSize.helper";
import { loadCanvasColors } from "../Helpers/loadCanvasColors.helper";
import { templateHandleFunctions } from "../Helpers/templateHandleFunctions.helper";

const TemplateModal = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    templateName: "",
    screenSize: "",
    selectableColor: "",
    templateType: "single",
    screenOrientation: "no-rotation",
    rotation: "",
    truInkEMMSTemplate: false,
    customProperties: {
      mode: "landscape",
    },
  });
  const [canvasSizes, setCanvasSizes] = useState([]);
  const [canvasColors, setCanvasColors] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [rotation, setRotation] = useState("");
  const [isLoadingSizes, setIsLoadingSizes] = useState(false);
  const [isLoadingColors, setIsLoadingColors] = useState(false);
  const [error, setError] = useState(null);
  const truInkState = useSelector((state) => state.truInk);
  const navigate = useNavigate();
  const selectedSite = useSelector((state) => state.truInk.selectedSite);
  const siteId = selectedSite ? selectedSite.id : null;
  const tenantId = selectedSite ? selectedSite.tenantId : null;
  const rotationOptions = [{ value: "180", label: "180°" }];

  const { handleChange, handleConfirm } = templateHandleFunctions(
    formData,
    setFormData,
    truInkState,
    tenantId,
    siteId,
    canvasSizes,
    canvasColors,
    setIsSaving,
    setError,
    navigate,
    onClose,
    rotation
  );
  useEffect(() => {
    if (!open) {
      setFormData({
        templateName: "",
        screenSize: "",
        selectableColor: "",
        templateType: "single",
        screenOrientation: "no-rotation",
        rotation: "",
        truInkEMMSTemplate: false,
        customProperties: {
          mode: "landscape",
        },
      });
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (open && tenantId) {
      loadCanvasSizes(
        setIsLoadingSizes,
        setCanvasSizes,
        setFormData,
        setError,
        tenantId
      );
    }
  }, [open, tenantId]);

  useEffect(() => {
    if (open && tenantId) {
      loadCanvasColors(
        tenantId,
        setIsLoadingColors,
        setCanvasColors,
        setFormData
      );
    }
  }, [open, tenantId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: "8px",
          padding: "8px",
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div" sx={{ fontWeight: "bold" }}>
          Add template
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {/* Template Name */}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
              * Template name
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Template name"
              value={formData.templateName}
              onChange={handleChange("templateName")}
              error={error && !formData.templateName.trim()}
              helperText={error && !formData.templateName.trim() ? error : ""}
            />

          </Box>

          {/* Screen Size */}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
              * Screen size
            </Typography>
            <FormControl
              fullWidth
              size="small"
              error={error && !formData.screenSize}
            >
              {isLoadingSizes ? (
                <Typography variant="body2">Loading screen sizes...</Typography>
              ) : (
                <Select
                  value={formData.screenSize}
                  onChange={handleChange("screenSize")}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select screen size
                  </MenuItem>
                  {canvasSizes.map((size) => (
                    <MenuItem
                      key={size.id || size.resolution}
                      value={size.resolution}
                    >
                      {size.name || size.resolution}
                      {size.isDefault && " (Default)"}
                      {size.description && ` - ${size.description}`}
                    </MenuItem>
                  ))}
                </Select>
              )}
              {error && !formData.screenSize && (
                <FormHelperText>{error}</FormHelperText>
              )}
            </FormControl>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
              Mode
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={formData.customProperties.mode}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    customProperties: {
                      ...prev.customProperties,
                      mode: e.target.value,
                    },
                  }))
                }
                displayEmpty
              >
                <MenuItem value="landscape">Landscape</MenuItem>
                <MenuItem value="portrait">Portrait</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
              Rotation
            </Typography>
            <FormControl
              fullWidth
              size="small"
              error={error && !formData.screenSize}
            >
              {isLoadingSizes ? (
                <Typography variant="body2">Loading screen sizes...</Typography>
              ) : (
                <Select
                  value={rotation}
                  onChange={(e) => setRotation(e.target.value)}
                  label="Rotation"
                >
                  {rotationOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
              {error && !formData.rotation && (
                <FormHelperText>{error}</FormHelperText>
              )}
            </FormControl>
          </Box>

          {/* Selectable Color */}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
              * Selectable color
            </Typography>
            <FormControl
              fullWidth
              size="small"
              error={error && !formData.selectableColor}
            >
              {isLoadingColors ? (
                <Typography variant="body2">Loading colors...</Typography>
              ) : (
                <Select
                  value={formData.selectableColor}
                  onChange={handleChange("selectableColor")}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select color set
                  </MenuItem>
                  {canvasColors.map((color) => (
                    <MenuItem key={color.id} value={color.id}>
                      {color.name}
                      {color.isDefault && " (Default)"}
                    </MenuItem>
                  ))}
                </Select>
              )}
              {error && !formData.selectableColor && (
                <FormHelperText>{error}</FormHelperText>
              )}
            </FormControl>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Checkbox
              label="Enable TruInk EMMS Template"
              checked={formData.truInkEMMSTemplate}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  truInkEMMSTemplate: e.target.checked ? true : false,
                }))
              }
            />
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
              {" "}
              TruInk EMMS Template
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <Divider sx={{ my: 1 }} />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            borderRadius: "4px",
            textTransform: "none",
            fontWeight: "bold",
            textTransform: "none",
            background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
            color: "#fff",
            boxShadow: "none",
            "&:hover": {
              background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
              opacity: 0.95,
              boxShadow: "none"
            },
            "&.Mui-disabled": {
              background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
              opacity: 0.5,
              color: "#fff"
            }

          }}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          sx={{
            borderRadius: "4px",
            textTransform: "none",
            fontWeight: "bold",
            textTransform: "none",
            backgroundColor: "rgb(90, 28, 47)",
            "&:hover": {
              backgroundColor: "rgb(70, 20, 35)",
            },
            "&.Mui-disabled": {
              backgroundColor: "rgba(90, 28, 47, 0.4)",
              color: "#fff",
            },

          }}
          disabled={isSaving || isLoadingSizes || isLoadingColors}
        >
          {isSaving ? "Creating..." : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateModal;
